import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton } from "@mui/lab";
import { Button, DialogActions, DialogContent } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { Control, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useUpdateUserUserTypeMutation } from "@/requests/user.mutations";
import { useUsersShowUpdateUserTypeDialog, useUsersTableActions } from "@/stores/use-users-table";
import { User, UserType } from "@/types/user.types";

import { UserTypeField } from "../userCreateDialog";

const formSchema = z.object({
    userType: z.nativeEnum(UserType).nullable(),
});

export type UpdateVehicleType = z.infer<typeof formSchema>;
function UpdateVehicleDialog() {
    const { t } = useTranslation();
    const { updateShowdUpdateUserTypeDialog } = useUsersTableActions();
    const user = useUsersShowUpdateUserTypeDialog() as User;
    const { isPending: loading, mutateAsync } = useUpdateUserUserTypeMutation();

    const {
        handleSubmit,
        formState: { errors },
        control,
        watch,
    } = useForm({
        defaultValues: {
            userType: user?.userType || null,
        },
        disabled: loading,
        resolver: zodResolver(formSchema),
    });

    const createUser = async (data: UpdateVehicleType) => {
        if (loading || !user.id || !data.userType) return;
        try {
            const res = await mutateAsync({ userType: data.userType, id: user.id });
            const message = res.message || "";
            if (res.ok) {
                handleClose();
                enqueueSnackbar(message, {
                    variant: "success",
                });
            } else {
                enqueueSnackbar(message, {
                    variant: "error",
                });
            }
        } catch (error) {
            console.error("User error:", error);
        }
    };
    const userType = watch("userType") as UserType;
    const handleClose = () => {
        updateShowdUpdateUserTypeDialog();
    };
    if (!user) return null;

    return (
        <>
            <DialogContent dividers>
                <form autoComplete="off" onSubmit={handleSubmit(createUser)} className="grid grid-cols-4 gap-7">
                    <UserTypeField
                        control={control as Control<UpdateVehicleType>}
                        errors={errors}
                        userType={userType}
                        loading={loading}
                    />
                </form>
            </DialogContent>
            <DialogActions>
                <Button disabled={loading} autoFocus color="secondary" variant="outlined" onClick={handleClose}>
                    {t("buttons.cancel")}
                </Button>

                <LoadingButton
                    disabled={user?.userType === userType || !userType}
                    loading={loading}
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit(createUser)}
                >
                    {t("buttons.update")}
                </LoadingButton>
            </DialogActions>
        </>
    );
}

export default UpdateVehicleDialog;
